import { Button } from "@ondit/ui";
import { X } from "lucide-react";
import { Link } from "react-router";
import type { GenerationJob, PageSummary } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";

export function GenerationProgress({
	entityId,
	jobs,
	pages,
	onDismiss,
}: {
	entityId: string;
	jobs: GenerationJob[];
	pages: PageSummary[];
	onDismiss: (id: string) => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	// A finished draft already has its own row and actions below.
	const visible = jobs.filter((job) => {
		const page = pages.find((page) => page.id === job.page_id);
		return !(job.status === "done" && page && (page.status === "draft" || page.status === "review"));
	});
	if (!visible.length) return null;
	return (
		<section className="generation-progress" aria-label={t("pages.generations")} aria-live="polite">
			{visible.map((job) => {
				const page = pages.find((page) => page.id === job.page_id);
				const active = job.status === "queued" || job.status === "running";
				return (
					<div className="generation-progress-row" key={job.id}>
						<span className="generation-progress-title">
							{job.title ?? `${t(`compose.recipe.${job.recipe}`)}${job.target ? ` · ${job.target}` : ""}`}
						</span>
						<span className="pill">
							{t(
								job.status === "done" && page?.status === "published"
									? "pages.status.published"
									: `pages.generationStatus.${job.status}`,
							)}
						</span>
						{active ? (
							<time className="faint" dateTime={job.created_at} title={new Date(job.created_at).toLocaleString(locale)}>
								{t("pages.generationStarted", { time: fmt.relative(locale, job.created_at) ?? "—" })}
							</time>
						) : null}
						{job.status === "failed" || job.status === "skipped" ? (
							<span className="faint">
								{job.reason ? t(`errors.gated.${job.reason}`) : t(`pages.generation.${job.status}`)}
							</span>
						) : null}
						{job.status === "done" && job.page_id ? (
							<Button asChild variant="link">
								<Link to={`/w/${entityId}/pages/${job.page_id}`}>{t("pages.viewArticle")}</Link>
							</Button>
						) : null}
						{!active ? (
							<Button variant="ghost" size="icon" aria-label={t("common.close")} onClick={() => onDismiss(job.id)}>
								<X />
							</Button>
						) : null}
					</div>
				);
			})}
		</section>
	);
}
